/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useEffect } from 'react'
import clsx from 'clsx'

import { ZinIconButton, ZinButton } from '@tnl/zin-react'
import { FormField } from '@shared/components/form/FormField'
import { useForm, type TFormModel } from '@hooks/useForm'
import { useSignInContext } from '@account/hooks/useSignInContext'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { useAuthLabelsContext } from '@account/contexts/AuthLabelsContext'
import { SIGNIN_LABELS } from '@constants'
import { DataStore } from '@utils/DataStore'
// Dynamic import to avoid dependency cycle
// const { getStepField } = await import('../stepRegistry')

export interface LoginFieldsProps extends React.HTMLAttributes<HTMLFormElement> {
  preSignIn?: () => void
  postSignIn?: () => void
  onUsernameChange?: (username: string) => void
  signInButtonText?: string
  fieldDefinitions?: {
    username?: any
    password?: any
    rememberMe?: any
    submitButton?: any
  }
  idPrefix?: string // Add optional ID prefix to prevent conflicts
}

export const LoginFields: React.FC<LoginFieldsProps> = ({ preSignIn, postSignIn, onUsernameChange, signInButtonText, fieldDefinitions, idPrefix }) => {
  const { getLabel } = useAuthLabelsContext()
  const { state, updateField, submit: submitContext } = useSignInContext()
  const { isAuthenticated, isAuthorized } = useAuthentication()

  const { formData, loading } = state

  const [showPassword, setShowPassword] = React.useState(false)
  const isSubmittingRef = React.useRef(false)

  const { username: usernameField, password: passwordField, rememberMe: rememberMeField, submitButton: submitButtonField } = fieldDefinitions || {}

  // Generate unique IDs using optional prefix
  const generateId = (baseId: string) => (idPrefix ? `${idPrefix}-${baseId}` : baseId)

  const submitButtonRef = React.useRef<HTMLButtonElement>(null)

  // Create form model for useForm (labels from auth labels API with fallback)
  const formModel = useMemo<TFormModel>(
    () => ({
      fields: {
        username: {
          id: usernameField?.id || generateId('username'),
          name: usernameField?.name || 'username',
          label: getLabel('signIn', 'loginForm', 'username', usernameField?.label ?? SIGNIN_LABELS.USERNAME),
          type: 'text',
          required: true,
          autoFocus: usernameField?.autoFocus !== false,
        },
        password: {
          id: passwordField?.id || generateId('password'),
          name: passwordField?.name || 'password',
          label: getLabel('signIn', 'loginForm', 'password', passwordField?.label ?? SIGNIN_LABELS.PASSWORD),
          type: 'password',
          required: true,
        },
        rememberMe: {
          id: rememberMeField?.id || generateId('rememberMe'),
          name: rememberMeField?.name || 'rememberMe',
          label: getLabel('signIn', 'loginForm', 'rememberMe', rememberMeField?.label ?? SIGNIN_LABELS.REMEMBER_ME),
          type: 'checkbox',
          required: false,
        },
      },
      initialState: {
        username: String(formData.username?.value || ''),
        password: String(formData.password?.value || ''),
        rememberMe: Boolean(formData.rememberMe?.value),
      },
    }),
    [formData, usernameField, passwordField, rememberMeField, idPrefix, getLabel],
  )

  const form = useForm(formModel)

  // Determine autocomplete behavior based on rememberMe checkbox
  const isRememberMeChecked = Boolean(form.values.rememberMe)
  const autocompleteMode = isRememberMeChecked ? 'on' : 'off'
  const usernameAutocomplete = isRememberMeChecked ? usernameField?.autoComplete || 'username' : 'off'
  const passwordAutocomplete = isRememberMeChecked ? passwordField?.autoComplete || 'current-password' : 'off'

  // Track if we're syncing from context to avoid loops
  const isSyncingFromContext = React.useRef(false)
  // Track previous context values to detect reset
  const prevContextValuesRef = React.useRef({
    username: String(formData.username?.value || ''),
    password: String(formData.password?.value || ''),
    rememberMe: Boolean(formData.rememberMe?.value),
  })

  // Sync FROM context TO form when context is reset (e.g., on account lockout)
  // This ensures the form clears when resetContext() is called
  useEffect(() => {
    if (isSyncingFromContext.current) return

    const contextUsername = String(formData.username?.value || '')
    const contextPassword = String(formData.password?.value || '')
    const contextRememberMe = Boolean(formData.rememberMe?.value)

    const prevValues = prevContextValuesRef.current
    const wasReset =
      // Previous values had data but now they're all empty (reset happened)
      (prevValues.username || prevValues.password || prevValues.rememberMe) && !contextUsername && !contextPassword && !contextRememberMe

    // Only sync FROM context TO form when a reset is detected
    // This prevents infinite loops while ensuring form clears on reset
    if (wasReset && (form.values.username || form.values.password || form.values.rememberMe)) {
      isSyncingFromContext.current = true
      form.setValue('username', '')
      form.setValue('password', '')
      form.setValue('rememberMe', false)
      onUsernameChange?.('')
      // Reset the flag after state updates
      setTimeout(() => {
        isSyncingFromContext.current = false
      }, 0)
    }

    // Update previous values ref
    prevContextValuesRef.current = {
      username: contextUsername,
      password: contextPassword,
      rememberMe: contextRememberMe,
    }
  }, [formData.username?.value, formData.password?.value, formData.rememberMe?.value, form, onUsernameChange])

  // Sync useForm values with useSignInContext for localStorage and state management
  // Only sync when form values change (user input), not when context updates.
  // formData.* is intentionally excluded from deps to prevent infinite sync loop between form and context.
  useEffect(() => {
    if (isSyncingFromContext.current) return
    const contextUsername = String(formData.username?.value || '')
    if (form.values.username !== contextUsername) {
      updateField('username', form.values.username)
      onUsernameChange?.(form.values.username)
    }
  }, [form.values.username, updateField, onUsernameChange])

  // formData.password excluded from deps to prevent infinite sync loop (see comment above)
  useEffect(() => {
    if (isSyncingFromContext.current) return
    const contextPassword = String(formData.password?.value || '')
    if (form.values.password !== contextPassword) {
      updateField('password', form.values.password)
    }
  }, [form.values.password, updateField])

  // formData.rememberMe excluded from deps to prevent infinite sync loop (see comment above)
  useEffect(() => {
    if (isSyncingFromContext.current) return
    const contextRememberMe = Boolean(formData.rememberMe?.value)
    if (form.values.rememberMe !== contextRememberMe) {
      updateField('rememberMe', form.values.rememberMe)
    }
  }, [form.values.rememberMe, updateField])

  // Handle username localStorage persistence (when rememberMe is checked)
  useEffect(() => {
    if (isRememberMeChecked && form.values.username) {
      DataStore.setItem('savedUsername', String(form.values.username), { store: localStorage })
    } else if (!isRememberMeChecked) {
      DataStore.removeItem('savedUsername', { store: localStorage })
    }
  }, [isRememberMeChecked, form.values.username])

  // Handle rememberMe localStorage persistence
  useEffect(() => {
    if (form.values.rememberMe) {
      DataStore.setItem('rememberMe', 'true', { store: localStorage })
    } else {
      DataStore.removeItem('rememberMe', { store: localStorage })
    }
  }, [form.values.rememberMe])

  // Calculate canSubmit based on useForm validation
  const canSubmit = useMemo(() => {
    const hasUsername = form.values.username && String(form.values.username).trim().length > 0
    const hasPassword = form.values.password && String(form.values.password).trim().length > 0
    const hasNoErrors = !form.errors.username && !form.errors.password
    return hasUsername && hasPassword && hasNoErrors
  }, [form.values.username, form.values.password, form.errors])

  // Focus the submit button when both username and password have values
  useEffect(() => {
    const hasUsername = form.values.username && String(form.values.username).trim()
    const hasPassword = form.values.password && String(form.values.password).trim()

    if (hasUsername && hasPassword && submitButtonRef.current && canSubmit) {
      // Use setTimeout to ensure the DOM is updated before focusing
      setTimeout(() => {
        if (submitButtonRef.current) {
          submitButtonRef.current.focus()
        }
      }, 0)
    }
  }, [form.values.username, form.values.password, canSubmit])

  // Handle form submission
  const handleSubmit = form.handleSubmit(async (values) => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    try {
      // Ensure context state is synced before submission
      updateField('username', values.username)
      updateField('password', values.password)
      updateField('rememberMe', values.rememberMe)

      await preSignIn?.()
      await submitContext()
      // Only call postSignIn if the full login process is successful
      // This means both authentication AND authorization must be complete
      if (!state.error && isAuthenticated && isAuthorized) {
        await postSignIn?.()
      }
    } finally {
      isSubmittingRef.current = false
    }
  })

  // Allow form submission on Enter press
  const handleKeyPress = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      handleSubmit(e)
    }
  }

  return (
    <form
      ref={form.formRef}
      id={generateId('signInForm')}
      className={clsx('signInForm flex flex-col justify-evenly gap-5')}
      noValidate
      onSubmit={handleSubmit}
      autoComplete={autocompleteMode}
      onKeyDown={handleKeyPress}>
      <div data-testid="mk-username-input">
        <FormField
          field={{
            ...formModel.fields.username,
            autoComplete: usernameAutocomplete,
            placeholder: usernameField?.placeholder || '',
          }}
          form={form}
          deferZinInputMount
        />
      </div>

      <div
        className="relative"
        data-testid="mk-password-input">
        <FormField
          field={{
            ...formModel.fields.password,
            type: showPassword ? 'text' : 'password',
            autoComplete: passwordAutocomplete,
            placeholder: passwordField?.placeholder || '',
          }}
          form={form}
          deferZinInputMount
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-auto">
          <ZinIconButton
            icon={showPassword ? 'eye-slash' : 'eye'}
            title={showPassword ? SIGNIN_LABELS.HIDE_PASSWORD : SIGNIN_LABELS.SHOW_PASSWORD}
            onClick={() => setShowPassword(!showPassword)}
            type="button"
            variant="ghost"
            size="sm"
          />
        </div>
      </div>

      <div className="flex items-center">
        <FormField
          field={formModel.fields.rememberMe}
          form={form}
        />
      </div>

      <ZinButton
        ref={submitButtonRef}
        type="submit"
        disabled={!canSubmit || loading}
        loading={loading}
        className="w-full">
        {signInButtonText || getLabel('signIn', 'loginForm', 'submitButton', submitButtonField?.label ?? SIGNIN_LABELS.SIGN_IN_SUBMIT_BUTTON_TITLE)}
      </ZinButton>
    </form>
  )
}
